import { MonitorSmartphone, Cpu, Smartphone, Camera, Battery, CircuitBoard, HardDrive, Wifi } from 'lucide-react';
import { z } from 'zod';

// src/data/components.ts
var manufacturers = {
  os: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."],
  cpu: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."],
  gpu: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."],
  camera: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."],
  battery: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."],
  ram: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."],
  storage: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."],
  network: ["Ayaya Inc.", "Bee Corp", "Cee Incorporated", "DeeDee Inc."]
};
var generateSystematicComponents = (categoryName) => {
  const components2 = [];
  components2.push({
    name: `A10 ${categoryName}`,
    value: `a10-${categoryName.toLowerCase().replace(" ", "-")}`,
    specs: `Entry-level ${categoryName}`,
    features: `Basic ${categoryName} functionality`,
    manufacturer: "Ayaya Inc.",
    releaseDate: { year: 2e3, month: 1, day: 1 },
    cost: 10,
    price: 20,
    techScore: 10,
    weight: 1,
    tier: "budget"
  });
  components2.push({
    name: `B30 ${categoryName}`,
    value: `b30-${categoryName.toLowerCase().replace(" ", "-")}`,
    specs: `Mid-range ${categoryName}`,
    features: `Standard ${categoryName} performance`,
    manufacturer: "Bee Corp",
    releaseDate: { year: 2e3, month: 1, day: 1 },
    cost: 30,
    price: 60,
    techScore: 30,
    weight: 1,
    tier: "mid"
  });
  components2.push({
    name: `C50 ${categoryName}`,
    value: `c50-${categoryName.toLowerCase().replace(" ", "-")}`,
    specs: `Premium ${categoryName}`,
    features: `Advanced ${categoryName} capabilities`,
    manufacturer: "Cee Incorporated",
    releaseDate: { year: 2e3, month: 1, day: 1 },
    cost: 50,
    price: 100,
    techScore: 50,
    weight: 1,
    tier: "premium"
  });
  components2.push({
    name: `D70 ${categoryName}`,
    value: `d70-${categoryName.toLowerCase().replace(" ", "-")}`,
    specs: `Flagship ${categoryName}`,
    features: `Cutting-edge ${categoryName} technology`,
    manufacturer: "DeeDee Inc.",
    releaseDate: { year: 2e3, month: 1, day: 1 },
    cost: 70,
    price: 140,
    techScore: 70,
    weight: 1,
    tier: "ultra"
  });
  return components2;
};
var components = [
  {
    id: "os",
    name: "Operating System",
    icon: MonitorSmartphone,
    weight: 0.8,
    options: generateSystematicComponents("OS").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "1.0GHz, Basic UI" : comp.name.includes("B30") ? "1.5GHz, Standard UI" : comp.name.includes("C50") ? "2.0GHz, Premium UI" : "2.5GHz, Flagship UI",
      features: comp.name.includes("A10") ? "Essential features, Basic apps" : comp.name.includes("B30") ? "Standard features, App ecosystem" : comp.name.includes("C50") ? "Advanced features, AI integration" : "Cutting-edge features, Full AI suite"
    }))
  },
  {
    id: "cpu",
    name: "Processor",
    icon: Cpu,
    weight: 1.5,
    options: generateSystematicComponents("CPU").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "1.2GHz Dual-core" : comp.name.includes("B30") ? "2.0GHz Quad-core" : comp.name.includes("C50") ? "2.8GHz Hexa-core" : "3.5GHz Octa-core"
    }))
  },
  {
    id: "gpu",
    name: "Graphics",
    icon: Smartphone,
    weight: 1.2,
    options: generateSystematicComponents("GPU").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "400MHz Graphics" : comp.name.includes("B30") ? "700MHz Graphics" : comp.name.includes("C50") ? "1.0GHz Graphics" : "1.5GHz Graphics, RT cores"
    }))
  },
  {
    id: "camera",
    name: "Camera",
    icon: Camera,
    weight: 1.3,
    options: generateSystematicComponents("Camera").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "8MP, f/2.4" : comp.name.includes("B30") ? "12MP, f/2.0" : comp.name.includes("C50") ? "48MP, f/1.8, OIS" : "108MP, f/1.6, OIS, 8K"
    }))
  },
  {
    id: "battery",
    name: "Battery",
    icon: Battery,
    weight: 1.4,
    options: generateSystematicComponents("Battery").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "2500mAh, 15W charging" : comp.name.includes("B30") ? "3500mAh, 30W charging" : comp.name.includes("C50") ? "4500mAh, 65W charging" : "5000mAh, 120W charging"
    }))
  },
  {
    id: "ram",
    name: "Memory",
    icon: CircuitBoard,
    weight: 1,
    options: generateSystematicComponents("RAM").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "2GB LPDDR4" : comp.name.includes("B30") ? "4GB LPDDR4X" : comp.name.includes("C50") ? "8GB LPDDR5" : "12GB LPDDR5X"
    }))
  },
  {
    id: "storage",
    name: "Storage",
    icon: HardDrive,
    weight: 0.9,
    options: generateSystematicComponents("Storage").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "32GB eMMC" : comp.name.includes("B30") ? "128GB UFS 2.1" : comp.name.includes("C50") ? "256GB UFS 3.1" : "512GB UFS 4.0"
    }))
  },
  {
    id: "network",
    name: "Network",
    icon: Wifi,
    weight: 1.1,
    options: generateSystematicComponents("Network").map((comp) => ({
      ...comp,
      specs: comp.name.includes("A10") ? "3G/WiFi 4" : comp.name.includes("B30") ? "4G LTE/WiFi 5" : comp.name.includes("C50") ? "5G Sub-6/WiFi 6" : "5G mmWave/WiFi 6E"
    }))
  }
];
var getComponentCost = (categoryId, componentValue) => {
  const category = components.find((cat) => cat.id === categoryId);
  if (!category) return 0;
  const component = category.options.find((opt) => opt.value === componentValue);
  return component?.cost || 0;
};
var getComponentTechScore = (categoryId, componentValue) => {
  const category = components.find((cat) => cat.id === categoryId);
  if (!category) return 0;
  const component = category.options.find((opt) => opt.value === componentValue);
  return component?.techScore || 0;
};

// src/data/camera.ts
var cameraStyles = [
  { name: "Pill", value: "pill" },
  { name: "Circle", value: "circle" },
  { name: "Square", value: "square" },
  { name: "Triple", value: "triple" }
];

// src/data/materials.ts
var materialOptions = [
  // Base materials (always available)
  { name: "Plastic", value: "plastic" },
  { name: "Aluminum", value: "aluminum" },
  // Research-gated materials (Tier 1)
  { name: "Matte", value: "matte", requiresResearch: "design_material_matte" },
  { name: "Glossy", value: "glossy", requiresResearch: "design_material_glossy" },
  { name: "Metallic", value: "metallic", requiresResearch: "design_material_metallic" },
  { name: "Glass", value: "glass", requiresResearch: "design_material_glass" },
  // Research-gated materials (Tier 2)
  { name: "Titanium", value: "titanium", requiresResearch: "design_material_titanium" },
  { name: "Ceramic", value: "ceramic", requiresResearch: "design_material_ceramic" },
  { name: "Wood", value: "wood", requiresResearch: "design_material_wood" },
  // Research-gated materials (Tier 3)
  { name: "Sapphire Glass", value: "sapphire", requiresResearch: "design_material_sapphire" },
  { name: "Carbon Fiber", value: "carbon_fiber", requiresResearch: "design_materials_exotic" },
  { name: "Transparent", value: "transparent", requiresResearch: "design_transparent" }
];

// src/utils/numberFormatter.ts
var abbreviations = [
  { value: 1e12, suffix: "T" },
  { value: 1e9, suffix: "B" },
  { value: 1e6, suffix: "M" },
  { value: 1e3, suffix: "K" }
];
function formatLargeNumber(value, options = {}) {
  const {
    decimalPlaces = 1,
    useAbbreviation = true,
    minimumValue = 1e3
  } = options;
  if (!useAbbreviation || Math.abs(value) < minimumValue) {
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalPlaces
    });
  }
  for (const { value: threshold, suffix } of abbreviations) {
    if (Math.abs(value) >= threshold) {
      const abbreviated = value / threshold;
      return `${abbreviated.toFixed(decimalPlaces)}${suffix}`;
    }
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces
  });
}
function formatNumber(value, options = {}) {
  return formatLargeNumber(value, options);
}
function formatCompactNumber(value) {
  return formatLargeNumber(value, {
    decimalPlaces: 1,
    useAbbreviation: true,
    minimumValue: 1e3
  });
}
function formatDetailedNumber(value) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}
function roundToThousandths(value) {
  return Math.round(value * 1e3) / 1e3;
}

// src/services/ProductMetricsService.ts
var PRODUCT_METRICS_CONSTANTS = {
  basePriceReference: 999,
  techScoreWeight: 0.7,
  priceRatioWeight: 0.3,
  userSatisfactionTechWeight: 0.6,
  userSatisfactionValueWeight: 0.4,
  satisfactionMultiplier: 0.9,
  qualityBaseMultiplier: 0.8,
  complexityFactorMin: 0.9,
  complexityFactorMax: 1.1
};
var QUALITY_CONSTANTS = {
  baseDefectRate: 5,
  techScoreDefectReduction: 0.04,
  defectRateVariation: 2,
  minDefectRate: 0.1,
  maxDefectRate: 10
};
var PRODUCTION_CONSTANTS = {
  manufacturingOverhead: 0.15,
  assemblyCost: 0.1,
  qualityControlCost: 0.05
};
var ProductMetricsService = class {
  static getComponentCost(categoryId, value) {
    const category = components.find((c) => c.id === categoryId);
    const option = category?.options.find((o) => o.value === value);
    return option?.cost || 0;
  }
  static getComponentTechScore(categoryId, value) {
    const category = components.find((c) => c.id === categoryId);
    const option = category?.options.find((o) => o.value === value);
    return (option?.techScore || 0) * (category?.weight || 1);
  }
  static getTotalComponentWeights() {
    return components.reduce((sum, category) => sum + category.weight, 0);
  }
  static calculateTotalCost(selectedComponents) {
    return Object.entries(selectedComponents).reduce((total, [categoryId, value]) => {
      return total + this.getComponentCost(categoryId, value);
    }, 0);
  }
  static calculateTechScore(selectedComponents) {
    const totalScore = Object.entries(selectedComponents).reduce((total, [categoryId, value]) => {
      return total + this.getComponentTechScore(categoryId, value);
    }, 0);
    const totalWeight = this.getTotalComponentWeights();
    return roundToThousandths(totalScore / totalWeight);
  }
  static calculateValueScore(techScore, price) {
    const basePrice = PRODUCT_METRICS_CONSTANTS.basePriceReference;
    const priceRatio = roundToThousandths(basePrice / price);
    const weightedScore = roundToThousandths(
      techScore * PRODUCT_METRICS_CONSTANTS.techScoreWeight + priceRatio * 100 * PRODUCT_METRICS_CONSTANTS.priceRatioWeight
    );
    return Math.round(Math.min(100, Math.max(0, weightedScore)));
  }
  static calculateUserSatisfaction(techScore, valueScore) {
    return Math.round(
      (techScore * PRODUCT_METRICS_CONSTANTS.userSatisfactionTechWeight + valueScore * PRODUCT_METRICS_CONSTANTS.userSatisfactionValueWeight) * PRODUCT_METRICS_CONSTANTS.satisfactionMultiplier
    );
  }
  static calculateQualityScore(product) {
    const baseQuality = product.designConfig.techScore * PRODUCT_METRICS_CONSTANTS.qualityBaseMultiplier;
    const complexityRange = PRODUCT_METRICS_CONSTANTS.complexityFactorMax - PRODUCT_METRICS_CONSTANTS.complexityFactorMin;
    const complexityFactor = PRODUCT_METRICS_CONSTANTS.complexityFactorMin + Math.random() * complexityRange;
    return Math.min(100, Math.round(baseQuality * complexityFactor));
  }
  static calculateDefectRate(product) {
    const reduction = product.designConfig.techScore * QUALITY_CONSTANTS.techScoreDefectReduction;
    const baseRate = QUALITY_CONSTANTS.baseDefectRate - reduction;
    const baseDefectRate = Math.max(
      QUALITY_CONSTANTS.minDefectRate,
      Math.min(QUALITY_CONSTANTS.maxDefectRate, baseRate)
    );
    const variation = (Math.random() - 0.5) * QUALITY_CONSTANTS.defectRateVariation;
    return roundToThousandths(Math.max(
      QUALITY_CONSTANTS.minDefectRate,
      Math.min(QUALITY_CONSTANTS.maxDefectRate, baseDefectRate + variation)
    ));
  }
  static calculateProductionCost(components2) {
    const componentsCost = this.calculateTotalCost(components2);
    const overhead = componentsCost * PRODUCTION_CONSTANTS.manufacturingOverhead;
    const assembly = componentsCost * PRODUCTION_CONSTANTS.assemblyCost;
    const qualityControl = componentsCost * PRODUCTION_CONSTANTS.qualityControlCost;
    return Math.round(componentsCost + overhead + assembly + qualityControl);
  }
};
var componentOptionSchema = z.object({
  name: z.string(),
  value: z.string(),
  specs: z.string().optional(),
  features: z.string().optional(),
  manufacturer: z.string(),
  releaseDate: z.object({
    year: z.number(),
    month: z.number(),
    day: z.number()
  }),
  cost: z.number(),
  price: z.number(),
  techScore: z.number(),
  weight: z.number(),
  tier: z.enum(["budget", "mid", "premium", "ultra"]).optional(),
  unlockDate: z.object({
    year: z.number(),
    month: z.number(),
    day: z.number()
  }).optional(),
  researchId: z.string().optional()
});
var lucideIconSchema = z.any();
var componentCategorySchema = z.object({
  id: z.union([
    z.literal("os"),
    z.literal("cpu"),
    z.literal("gpu"),
    z.literal("ram"),
    z.literal("storage"),
    z.literal("network"),
    z.literal("camera"),
    z.literal("battery")
  ]),
  name: z.string(),
  icon: lucideIconSchema,
  weight: z.number(),
  options: z.array(componentOptionSchema)
});
var productMetadataSchema = z.object({
  companyId: z.string(),
  designDate: z.object({
    day: z.number(),
    month: z.number(),
    year: z.number()
  }),
  releaseDate: z.object({
    day: z.number(),
    month: z.number(),
    year: z.number()
  }).optional(),
  discontinuedDate: z.object({
    day: z.number(),
    month: z.number(),
    year: z.number()
  }).optional(),
  isReleased: z.boolean().optional()
});
var productSalesMetricsSchema = z.object({
  unitsSold: z.number(),
  revenue: z.number(),
  averageRating: z.number(),
  marketShare: z.number()
});
var productReviewSchema = z.object({
  rating: z.number().min(0).max(5),
  text: z.string(),
  author: z.string(),
  publication: z.string().optional(),
  reviewerTitle: z.string().optional()
});
var productReviewsSchema = z.object({
  professional: z.array(productReviewSchema),
  customer: z.array(productReviewSchema)
});
var productValidationErrorSchema = z.object({
  field: z.string(),
  message: z.string()
});
var developmentPhaseSchema = z.union([
  z.literal("design"),
  z.literal("engineering"),
  z.literal("testing"),
  z.literal("manufacturing")
]);
var productDevelopmentPhaseSchema = z.object({
  phase: developmentPhaseSchema,
  startDate: z.object({
    day: z.number(),
    month: z.number(),
    year: z.number()
  }),
  estimatedDuration: z.number(),
  actualDuration: z.number().optional(),
  budget: z.number(),
  confidence: z.number(),
  qualityScore: z.number(),
  riskEvents: z.array(z.string()),
  investmentCount: z.number().default(0)
});
var investmentStateSchema = z.object({
  type: z.enum(["minor", "major"]).optional(),
  strikeCount: z.number().default(0),
  maxStrikes: z.number().default(3),
  lastUpdateTime: z.date().optional()
});
var phasePreferencesSchema = z.object({
  design_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask"),
  engineering_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask"),
  testing_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask"),
  manufacturing_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask")
});
var productPreferenceStateSchema = z.object({
  capturedPreferences: phasePreferencesSchema,
  capturedAt: z.date(),
  lastPhaseProcessed: z.date().optional(),
  investmentStates: z.object({
    design: investmentStateSchema.optional(),
    engineering: investmentStateSchema.optional(),
    testing: investmentStateSchema.optional(),
    manufacturing: investmentStateSchema.optional()
  }).default({})
});
var productDesignSchema = z.object({
  name: z.string(),
  price: z.number(),
  color: z.string(),
  material: z.string(),
  components: z.record(componentCategorySchema.shape.id, z.string()),
  design: z.object({
    buttonColor: z.string(),
    hasButtons: z.boolean(),
    screenBorderColor: z.string(),
    cameraStyle: z.string(),
    cameraColor: z.string(),
    cameraPosition: z.object({
      x: z.number(),
      y: z.number()
    }),
    buttonPositions: z.object({
      volume: z.number(),
      power: z.number()
    }),
    phoneSize: z.object({
      width: z.number(),
      height: z.number(),
      depth: z.number()
    }),
    screenSize: z.object({
      width: z.number(),
      height: z.number()
    }),
    cornerRadius: z.number()
  }),
  techScore: z.number(),
  marketAppeal: z.number()
});
var productSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  developmentCost: z.number(),
  productionCost: z.number(),
  price: z.number(),
  techScore: z.number(),
  marketAppeal: z.number(),
  status: z.union([
    z.literal("concept"),
    // Legacy status, will be migrated to "design"
    z.literal("development"),
    z.literal("design"),
    z.literal("engineering"),
    z.literal("testing"),
    z.literal("manufacturing"),
    z.literal("production"),
    z.literal("released"),
    z.literal("discontinued")
  ]),
  designConfig: productDesignSchema,
  metadata: productMetadataSchema,
  salesMetrics: productSalesMetricsSchema,
  reviews: productReviewsSchema.optional(),
  developmentPhase: productDevelopmentPhaseSchema.optional(),
  preferenceState: productPreferenceStateSchema.optional()
});
var productOperationResultSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  data: productSchema.optional()
});
var STARTING_YEAR = 2e3;
var gameDate = z.object({
  day: z.number().min(1).max(30),
  month: z.number().min(1).max(12),
  year: z.number().min(STARTING_YEAR),
  hour: z.number().min(0).max(23).optional(),
  minute: z.number().min(0).max(59).optional()
});
var companyMetrics = z.object({
  cash: z.number(),
  reputation: z.number().min(0).max(100),
  marketShare: z.number().min(0).max(1),
  researchPoints: z.number().min(0),
  bankruptcyWarnings: z.number().min(0).max(3),
  isGameOver: z.boolean()
});
var financialSchema = z.object({
  cash: z.number(),
  revenue: z.number(),
  costs: z.number(),
  profit: z.number(),
  dailyStats: z.object({
    revenue: z.number(),
    costs: z.number(),
    profit: z.number(),
    salesBreakdowns: z.array(z.object({
      productId: z.string(),
      revenue: z.number(),
      unitsSold: z.number()
    })).optional()
  }),
  monthlyStats: z.object({
    revenue: z.number(),
    costs: z.number(),
    profit: z.number()
  }),
  yearlyStats: z.object({
    revenue: z.number(),
    costs: z.number(),
    profit: z.number()
  })
});
var buildingSchema = z.object({
  id: z.string(),
  buildingTypeId: z.string(),
  purchaseDate: gameDate,
  constructionCompleteDate: gameDate.optional(),
  isUnderConstruction: z.boolean().default(false),
  name: z.string().optional()
});
var employeeAllocationSchema = z.object({
  productId: z.string(),
  phase: z.enum(["design", "engineering", "testing", "manufacturing", "production"]),
  employeeType: z.string(),
  count: z.number(),
  employeeIds: z.array(z.string()).optional()
});
var researchProgress = z.object({
  nodeId: z.string(),
  startDate: gameDate,
  progress: z.number().min(0).max(100),
  completed: z.boolean()
});
var employeeSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  salary: z.number(),
  skills: z.record(z.string(), z.number()),
  experience: z.number(),
  performance: z.number(),
  happiness: z.number()
});
var hiringProcessSchema = z.object({
  role: z.string(),
  count: z.number(),
  startDate: gameDate,
  daysRemaining: z.number()
});
var marketStateSchema = z.object({
  totalMarketSize: z.number(),
  marketGrowthRate: z.number(),
  economicPhase: z.string(),
  competitorActions: z.array(z.any()).default([]),
  technologicalDisruptions: z.array(z.any()).default([]),
  marketEvents: z.array(z.any()).default([]),
  playerActions: z.array(z.any()).default([])
});
var researchStateSchema = z.object({
  unlockedNodes: z.array(z.string()).default([]),
  activeResearch: z.array(researchProgress).default([]),
  researchPoints: z.number().min(0).default(0),
  monthlyInvestment: z.number().min(0).default(0),
  rpGenerationRate: z.number().min(1).default(1)
});
var eventStateSchema = z.object({
  eventHistory: z.array(z.any()).default([]),
  pendingReactiveEvents: z.array(z.any()).default([]),
  systemicNotifications: z.array(z.any()).default([]),
  marketConditions: z.record(z.string(), z.number()).default({})
});
var productionBatchSchema = z.object({
  id: z.string(),
  productId: z.string(),
  startDate: z.date(),
  targetUnits: z.number(),
  completedUnits: z.number(),
  qualityScore: z.number(),
  defectRate: z.number(),
  status: z.enum(["in-progress", "completed"]),
  costs: z.object({
    materials: z.number(),
    labor: z.number(),
    overhead: z.number()
  })
});
var dailyStatsSchema = z.object({
  revenue: z.number(),
  costs: z.number(),
  profit: z.number(),
  salesBreakdowns: z.array(z.object({
    productId: z.string(),
    unitsSold: z.number(),
    revenue: z.number()
  })).default([])
});
var monthlyFinancialSnapshotSchema = z.object({
  date: z.object({
    month: z.number().min(1).max(12),
    year: z.number().min(STARTING_YEAR)
  }),
  revenue: z.number(),
  costs: z.number(),
  profit: z.number(),
  cash: z.number()
});
var financialHistoryStateSchema = z.object({
  monthlySnapshots: z.array(monthlyFinancialSnapshotSchema).default([])
}).default({ monthlySnapshots: [] });
var currentMonthAccumulatorSchema = z.object({
  revenue: z.number(),
  costs: z.number()
}).default({ revenue: 0, costs: 0 });
var productionLineSchema = z.object({
  id: z.string(),
  factoryBuildingId: z.string(),
  lineNumber: z.number(),
  productId: z.string().nullable(),
  productName: z.string().optional(),
  lastProductChange: gameDate.optional(),
  allocatedEmployees: z.number(),
  dailyOutput: z.number(),
  efficiency: z.number(),
  qualityScore: z.number(),
  status: z.enum(["idle", "active", "paused", "maintenance"]),
  pauseReason: z.enum(["no_product", "no_employees", "no_components", "maintenance"]).optional()
});
var componentOrderSchema = z.object({
  orderId: z.string(),
  componentId: z.string(),
  componentName: z.string(),
  quantity: z.number(),
  unitCost: z.number(),
  totalCost: z.number(),
  orderDate: z.date(),
  expectedDeliveryDate: z.date(),
  deliveryDate: z.date().optional(),
  status: z.enum(["pending", "in_transit", "delivered", "cancelled"]),
  gameOrderDate: gameDate.optional(),
  gameDeliveryDate: gameDate.optional()
});
var autoOrderSettingSchema = z.object({
  componentId: z.string(),
  quantity: z.number(),
  orderOnZeroInventory: z.boolean().optional(),
  autoOrderThreshold: z.number().nullable(),
  recurringOrder: z.object({
    enabled: z.boolean(),
    frequency: z.enum(["month", "quarter", "year"]),
    lastOrderDate: gameDate.optional()
  })
});
var componentInventorySchema = z.object({
  id: z.string(),
  componentId: z.string(),
  componentName: z.string(),
  category: z.string(),
  currentStock: z.number(),
  minStock: z.number(),
  maxStock: z.number(),
  unitCost: z.number(),
  consumptionRate: z.number(),
  lastOrderDate: z.date().optional(),
  lastOrderQuantity: z.number().optional(),
  leadTimeDays: z.number()
});
var deviceInventorySchema = z.object({
  id: z.string(),
  productId: z.string(),
  productName: z.string(),
  currentStock: z.number(),
  productionRate: z.number(),
  targetStock: z.number(),
  qualityScore: z.number()
});
var inventoryStateSchema = z.object({
  components: z.array(componentInventorySchema).default([]),
  devices: z.array(deviceInventorySchema).default([]),
  pendingOrders: z.array(componentOrderSchema).default([]),
  autoOrderSettings: z.record(z.string(), autoOrderSettingSchema).default({})
});
var gameSaveSchema = z.object({
  version: z.string(),
  saveDate: z.date(),
  gameDate,
  company: z.object({
    id: z.string(),
    name: z.string(),
    ceoName: z.string(),
    founded: gameDate,
    metrics: companyMetrics,
    finances: financialSchema.optional(),
    research: researchStateSchema.optional(),
    products: z.array(productSchema),
    marketingCampaigns: z.array(z.any()),
    factories: z.array(z.any()),
    employees: z.array(employeeSchema).default([]),
    hiringProcesses: z.array(hiringProcessSchema).default([]),
    buildings: z.array(buildingSchema).default([]),
    employeeAllocations: z.array(employeeAllocationSchema).default([])
  }),
  marketState: marketStateSchema.optional(),
  eventState: eventStateSchema.optional(),
  productionBatches: z.array(productionBatchSchema).default([]),
  productionLines: z.array(productionLineSchema).default([]),
  competitorProducts: z.array(productSchema).default([]),
  dailyStats: dailyStatsSchema.optional(),
  inventoryState: inventoryStateSchema.optional(),
  financialHistory: financialHistoryStateSchema,
  currentMonthAccumulator: currentMonthAccumulatorSchema,
  settings: z.object({
    gameSpeed: z.number().min(1).max(5),
    autosaveEnabled: z.boolean(),
    autosaveInterval: z.number().min(1),
    fireWarningEnabled: z.boolean().default(true),
    phaseProgressionPreferences: z.object({
      design_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask"),
      engineering_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask"),
      testing_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask"),
      manufacturing_complete: z.enum(["proceed", "minor", "major", "ask"]).default("ask")
    }).default({
      design_complete: "ask",
      engineering_complete: "ask",
      testing_complete: "ask",
      manufacturing_complete: "ask"
    }),
    phasePreferencesLastChanged: z.date().optional(),
    audio: z.object({
      musicEnabled: z.boolean().default(true),
      musicVolume: z.number().min(0).max(1).default(0.5),
      currentTrack: z.number().min(0).default(0)
    }).default({
      musicEnabled: true,
      musicVolume: 0.5,
      currentTrack: 0
    }),
    tutorialCompleted: z.boolean().default(false),
    tutorialSkipped: z.boolean().default(false)
  }),
  statistics: z.object({
    totalRevenue: z.number().min(0),
    totalProfit: z.number(),
    productsLaunched: z.number().min(0),
    marketSharePeak: z.number().min(0).max(1),
    bankruptcies: z.number().min(0),
    playTime: z.number().min(0)
  })
});

// src/utils/getDefaultComponents.ts
function getDefaultComponents(gameDate2, completedResearch) {
  const defaults = {};
  const checkDate = !!gameDate2;
  components.forEach((category) => {
    const availableOptions = category.options.filter((option) => {
      if (option.researchId && (!completedResearch || !completedResearch.includes(option.researchId))) {
        return false;
      }
      if (checkDate && gameDate2 && option.releaseDate) {
        const currentDate = new Date(gameDate2.year, gameDate2.month - 1, gameDate2.day);
        const componentReleaseDate = new Date(
          option.releaseDate.year,
          option.releaseDate.month - 1,
          option.releaseDate.day
        );
        if (currentDate < componentReleaseDate) {
          return false;
        }
      }
      if (checkDate && gameDate2 && option.unlockDate) {
        const currentDate = new Date(gameDate2.year, gameDate2.month - 1, gameDate2.day);
        const componentUnlockDate = new Date(
          option.unlockDate.year,
          option.unlockDate.month - 1,
          option.unlockDate.day
        );
        if (currentDate < componentUnlockDate) {
          return false;
        }
      }
      return true;
    });
    if (availableOptions.length > 0) {
      const sortedOptions = [...availableOptions].sort((a, b) => a.cost - b.cost);
      defaults[category.id] = sortedOptions[0].value;
    }
  });
  return defaults;
}

export { ProductMetricsService, cameraStyles, componentCategorySchema, componentOptionSchema, components, developmentPhaseSchema, formatCompactNumber, formatDetailedNumber, formatLargeNumber, formatNumber, gameSaveSchema, getComponentCost, getComponentTechScore, getDefaultComponents, investmentStateSchema, manufacturers, materialOptions, phasePreferencesSchema, productDesignSchema, productDevelopmentPhaseSchema, productMetadataSchema, productOperationResultSchema, productPreferenceStateSchema, productReviewSchema, productReviewsSchema, productSalesMetricsSchema, productSchema, productValidationErrorSchema, roundToThousandths };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map